import { useCallback, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useCrmHost } from '../crm/CrmHostContext'
import { useIsNarrow } from '../hooks/useIsNarrow'
import { AccountLibraryDeps } from '../library/AccountLibraryDeps'
import { AccountChatsDeps } from './AccountChatsDeps'
import { useCrmMe } from './useCrmMe'
import { ChatsPage } from './ChatsPage'
import { ChatWindow } from './ChatWindow'
import type { HeaderPermissions } from './ChatHeader'

interface Props {
  accountId: string
  /** Collections write: the chat's list star (chat tabs are the person's own, ADR 0027). */
  canEditTabs: boolean
  /** The open chat's writes (rename, mute, hide, Limit, clear). */
  can: Omit<HeaderPermissions, 'editLists'>
  canReadStats: boolean
  mobileNav?: ReactNode
}

/** The console's Chats page for one OnlyFans account (ADR 0024): the list and the React chat. */
export function AccountChats({ accountId, canEditTabs, can, canReadStats, mobileNav }: Props) {
  const crm = useCrmHost()
  const { user } = useAuth()
  // The CRM's own id for the person is their sign-in id, so the list starts at
  // once; only the open chat needs the CRM username (to tell its own messages).
  const me = useCrmMe()
  const narrow = useIsNarrow()
  const account = crm.crmAccounts.find((a) => a.account_id === accountId)
  const modelName = account?.profile_name || account?.username || ''
  const reportUnread = crm.reportUnread
  const onUnread = useCallback((n: number) => reportUnread(accountId, n), [reportUnread, accountId])
  const navigate = useNavigate()
  const openWorkspace = useCallback(() => navigate('/workspace'), [navigate])

  return (
    <AccountLibraryDeps accountId={accountId}>
      <AccountChatsDeps accountId={accountId} crmUserId={user?.id ?? me?.id ?? ''}>
        <ChatsPage
          accountId={accountId}
          canReadStats={canReadStats}
          onUnread={onUnread}
          narrow={narrow}
          mobileNav={mobileNav}
          renderChat={(fanId, fanName, chat) =>
            me === undefined ? (
              <div className="cp-chat-loading" aria-busy="true" />
            ) : (
              <ChatWindow
                key={fanId}
                accountId={accountId}
                fanId={fanId}
                modelName={modelName}
                myUsername={me?.username ?? ''}
                fanNameHint={fanName || null}
                compact={narrow}
                can={{ ...can, editLists: canEditTabs }}
                onLeave={chat.leave}
                onBack={chat.back}
                phone={narrow ? { siblings: chat.siblings, onOpenSibling: chat.open, onMarkUnread: chat.markUnread } : undefined}
                // The side column opens chats here, or on the Account they belong to.
                onOpenChat={(acc, fan, name) =>
                  acc === accountId ? chat.open(fan, name) : navigate(`/account/${encodeURIComponent(acc)}/crm/chats?fan=${encodeURIComponent(fan)}`)
                }
                onOpenWorkspace={openWorkspace}
              />
            )
          }
        />
      </AccountChatsDeps>
    </AccountLibraryDeps>
  )
}
